import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, File, Download, Upload, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { pluginSettingsAPI } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const PluginSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ pdf: false, set: false });
  const [deleting, setDeleting] = useState({ pdf: false, set: false });
  const [deletingConfirm, setDeletingConfirm] = useState(null);
  const pdfInputRef = useRef(null);
  const setInputRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await pluginSettingsAPI.get();
      setSettings(response.data.data);
    } catch (error) {
      toast.error(t('pluginSettings.errors.fetch'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleFileChange = async (fileType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading((prev) => ({ ...prev, [fileType]: true }));
    try {
      const api = fileType === 'pdf' ? pluginSettingsAPI.uploadPdf : pluginSettingsAPI.uploadSet;
      const response = await api(file);
      setSettings(response.data.data);
      toast.success(t(fileType === 'pdf' ? 'pluginSettings.success.uploadPdf' : 'pluginSettings.success.uploadSet'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('pluginSettings.errors.upload'));
    } finally {
      setUploading((prev) => ({ ...prev, [fileType]: false }));
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deletingConfirm) return;
    const fileType = deletingConfirm;
    setDeleting((prev) => ({ ...prev, [fileType]: true }));
    try {
      const api = fileType === 'pdf' ? pluginSettingsAPI.deletePdf : pluginSettingsAPI.deleteSet;
      const response = await api();
      setSettings(response.data.data);
      toast.success(t('pluginSettings.success.delete'));
      setDeletingConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('pluginSettings.errors.delete'));
    } finally {
      setDeleting((prev) => ({ ...prev, [fileType]: false }));
    }
  };

  const handleDownload = async (fileType) => {
    try {
      const response = await pluginSettingsAPI.download(fileType);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', settings[`${fileType}File`].originalName || `${fileType}-file`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t('pluginSettings.errors.download'));
    }
  };

  const renderCard = (fileType, fileData, Icon, color, subtitleKey) => {
    const isUploading = uploading[fileType];
    const isDeleting = deleting[fileType];
    return (
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col items-center text-center max-w-xs">
        <div className={`w-20 h-20 rounded-full ${color} flex items-center justify-center shadow-md mb-4`}>
          <Icon className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">MetaTrader 5 Settings for 4000-5000 $</h2>
        <p className="text-sm text-gray-500 mb-4">{t(subtitleKey)}</p>

        {fileData ? (
          <>
            <div className="text-xs text-gray-500 mb-4 space-y-1">
              <p className="font-mono break-all">{fileData.originalName}</p>
              <p>{formatSize(fileData.size)} · {formatDate(fileData.uploadedAt)}</p>
            </div>

            <div className="flex gap-2 mb-3 w-full">
              <button
                type="button"
                onClick={() => handleDownload(fileType)}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t(fileType === 'pdf' ? 'pluginSettings.downloadPdf' : 'pluginSettings.downloadSet')}
              </button>
            </div>

            <input
              ref={fileType === 'pdf' ? pdfInputRef : setInputRef}
              type="file"
              accept={fileType === 'pdf' ? '.pdf,application/pdf' : '.set,application/octet-stream,text/plain'}
              onChange={(e) => handleFileChange(fileType, e)}
              className="hidden"
            />
            <div className="flex gap-2 w-full">
              <button
                type="button"
                onClick={() => (fileType === 'pdf' ? pdfInputRef : setInputRef).current?.click()}
                disabled={isUploading}
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {t('pluginSettings.replace')}
              </button>
              <button
                type="button"
                onClick={() => setDeletingConfirm(fileType)}
                disabled={isDeleting}
                className="px-3 py-2.5 bg-red-50 text-red-700 font-medium text-sm rounded-lg border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center"
                title={t('pluginSettings.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-4 italic">{t('pluginSettings.noFile')}</p>
            <input
              ref={fileType === 'pdf' ? pdfInputRef : setInputRef}
              type="file"
              accept={fileType === 'pdf' ? '.pdf,application/pdf' : '.set,application/octet-stream,text/plain'}
              onChange={(e) => handleFileChange(fileType, e)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => (fileType === 'pdf' ? pdfInputRef : setInputRef).current?.click()}
              disabled={isUploading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t('pluginSettings.upload')}
            </button>
          </>
        )}
      </div>
    );
  };

  if (loading && !settings) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('pluginSettings.title')}</h1>
        <p className="text-gray-500 text-sm mt-1">{t('pluginSettings.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-stretch pt-8 justify-center">
        {renderCard('pdf', settings?.pdfFile, FileText, 'bg-red-500', 'pluginSettings.pdfSubtitle')}
        {renderCard('set', settings?.setFile, File, 'bg-gray-600', 'pluginSettings.setSubtitle')}
      </div>

      <ConfirmModal
        isOpen={!!deletingConfirm}
        onClose={() => setDeletingConfirm(null)}
        onConfirm={handleDelete}
        title={t('pluginSettings.confirmDeleteTitle')}
        message={t(
          deletingConfirm === 'pdf'
            ? 'pluginSettings.confirmDeletePdf'
            : 'pluginSettings.confirmDeleteSet'
        )}
        confirmText={t('pluginSettings.delete')}
        submitting={deleting[deletingConfirm] || false}
      />
    </div>
  );
};

export default PluginSettings;
